import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Button, TextField, Typography, Container, AppBar, Toolbar, Checkbox,
    FormControlLabel, Dialog, DialogActions, DialogContent, DialogTitle, Box,
    MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import { styled } from '@mui/system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from './firebase';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const GOOGLE_PLACES_API_URL = `https://maps.googleapis.com/maps/api/place/autocomplete/json`;
const GOOGLE_PLACE_DETAILS_URL = `https://maps.googleapis.com/maps/api/place/details/json`;


// Custom Modal for selecting sports
const SelectSportsModal = ({ open, onClose, availableSports, selectedSports, onSportToggle }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Select Sports</DialogTitle>
        <DialogContent>
            <Box display="flex" flexDirection="column">
                {availableSports.map((sport) => (
                    <FormControlLabel
                        key={sport.name}
                        control={
                            <Checkbox
                                checked={selectedSports.includes(sport.name)}
                                onChange={() => onSportToggle(sport.name)}
                            />
                        }
                        label={sport.name}
                    />
                ))}
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} style={{ color: '#C63D57' }}>Done</Button>
        </DialogActions>
    </Dialog>
);

const Root = styled('div')({
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#141414',
});

const StyledAppBar = styled(AppBar)({
    backgroundColor: 'rgba(20, 20, 20, 0.8)',
    borderBottom: '1px solid #e6e6e6',
    zIndex: 10,
    position: 'fixed',
    top: 0,
    width: '100%',
});

const Logo = styled('img')({
    flexGrow: 1,
    height: 'auto',
    width: '20%',
    maxWidth: '100px',
    minWidth: '50px',
});

const StyledContainer = styled(Container)({
    textAlign: 'center',
    paddingTop: '2rem',
    paddingBottom: '6rem',
    zIndex: 2,
    marginTop: '4rem',
});

const FormContainer = styled(Container)({
    backgroundColor: 'rgba(51, 51, 51, 0.8)',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)',
    marginTop: '1rem',
    overflow: 'visible',
});

const StepIndicator = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '0.5rem',
    zIndex: 2,
});

const PlanIndicator = styled('div')({
    marginBottom: '1rem',
    zIndex: 2,
});

const ActionButton = styled(Button)({
    backgroundColor: '#C63D57',
    color: '#fff',
    '&:hover': {
        backgroundColor: '#a83348',
    },
    marginTop: '0.5rem',
});

const StyledTextField = styled(TextField)({
    marginBottom: '0.5rem',
});

const ConsentLabel = styled(Typography)({
    fontSize: '0.875rem',
    color: '#fff',
});

const WaiverContent = styled(Typography)({
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
});

const Footer = styled(Box)({
    width: '100%',
    padding: '2rem 0',
    textAlign: 'center',
    fontSize: '0.9rem',
    color: '#b0b0b0',
    borderTop: '1px solid #303030',
    backgroundColor: '#141414',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 'auto',
});

const CreateAccount = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedPlan } = location.state || { selectedPlan: 'No plan selected' };

    const [sportsModalOpen, setSportsModalOpen] = useState(false);

    // Form Data State
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        birthdate: '',
        gender: '',
        street: '',
        unit: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        sports: [],
        consent: false,
        waiverDialogOpen: false,
        waiverSigned: false,
        waiverAgreed: false,
        participantSignatureName: '',
        guardianSignatureName: '',
        referralCode: '',
    });

    // These state variables are still needed and remain the same
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [errors, setErrors] = useState({});
    const [availableSports, setAvailableSports] = useState([]);
    const inputRef = useRef(null);


    useEffect(() => {
        const fetchSports = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/sports`);
                setAvailableSports(response.data.sports);
            } catch (error) {
                console.error('Failed to fetch sports:', error);
            }
        };

        fetchSports();
    }, []);

    useEffect(() => {
        console.log("Rendered Suggestions:", addressSuggestions);
    }, [addressSuggestions]);

    useEffect(() => {
        setShowSuggestions(true);
    }, []);



    // Function to toggle the selection of a sport
    const toggleSportSelection = (sport) => {
        const updatedSports = formData.sports.includes(sport)
            ? formData.sports.filter((selectedSport) => selectedSport !== sport)
            : [...formData.sports, sport];

        setFormData((prevState) => ({
            ...prevState,
            sports: updatedSports,
        }));

        // Validation for sports selection
        if (updatedSports.length === 0) {
            setErrors((prevErrors) => ({ ...prevErrors, sports: 'At least one sport must be selected' }));
        } else {
            setErrors((prevErrors) => ({ ...prevErrors, sports: '' }));
        }
    };


    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;

        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });

        // Real-time validation for Zip Code
        if (name === 'zipCode' && !value) {
            setErrors((prevErrors) => ({ ...prevErrors, zipCode: 'Zip Code is required' }));
        } else if (name === 'zipCode') {
            setErrors((prevErrors) => ({ ...prevErrors, zipCode: '' }));
        }

        // Real-time validation for Sports selection
        if (name === 'sports' && formData.sports.length === 0) {
            setErrors((prevErrors) => ({ ...prevErrors, sports: 'At least one sport must be selected' }));
        } else if (name === 'sports') {
            setErrors((prevErrors) => ({ ...prevErrors, sports: '' }));
        }

        // Real-time validation for Country
        if (name === 'country' && !value) {
            setErrors((prevErrors) => ({ ...prevErrors, country: 'Country is required' }));
        } else if (name === 'country') {
            setErrors((prevErrors) => ({ ...prevErrors, country: '' }));
        }


    };

    const handleAddressInput = async (input) => {
        if (!input || input.trim().length < 3) {
            setShowSuggestions(false);
            setAddressSuggestions([]);
            return;
        }

        try {
            console.log(`Fetching suggestions for input: "${input}"`);

            const response = await axios.get('https://api.academy.marsreel.com/api/places/autocomplete', {
                params: { input: input.trim() }
            });

            console.log("✅ Full API Response:", response.data);

            // Check if response is an array
            if (Array.isArray(response.data)) {
                console.log("✅ Setting address suggestions:", response.data);
                setAddressSuggestions(response.data);
                setShowSuggestions(true);
            } else if (response.data && response.data.predictions) {
                console.log("✅ Predictions Found:", response.data.predictions);
                setAddressSuggestions(response.data.predictions);
                setShowSuggestions(true);
            } else {
                console.error("❌ Unexpected API response format:", response.data);
                setAddressSuggestions([]);
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error('❌ Error fetching address suggestions:', error);
            setAddressSuggestions([]);
            setShowSuggestions(false);
        }
    };


    const handleAddressSelect = async (placeId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/places/details`, {
                params: { placeId }
            });

            console.log("✅ Place Details Response:", response.data);

            if (response.data) {
                const addressComponents = response.data.address_components;

                // Extract street name only
                const streetNumber = addressComponents.find(c => c.types.includes("street_number"))?.long_name || "";
                const streetName = addressComponents.find(c => c.types.includes("route"))?.long_name || "";
                const city = addressComponents.find(c => c.types.includes("locality"))?.long_name || "";
                const state = addressComponents.find(c => c.types.includes("administrative_area_level_1"))?.short_name || "";
                const zipCode = addressComponents.find(c => c.types.includes("postal_code"))?.long_name || "";
                const country = addressComponents.find(c => c.types.includes("country"))?.long_name || "";

                const formattedStreet = `${streetNumber} ${streetName}`.trim();

                console.log("✅ Extracted Address Details:", { formattedStreet, city, state, zipCode, country });

                setFormData(prevState => ({
                    ...prevState,
                    street: formattedStreet,
                    city,
                    state,
                    zipCode,
                    country
                }));

                setShowSuggestions(false);
            } else {
                console.error("❌ No address details found in response:", response.data);
            }
        } catch (error) {
            console.error("❌ Error fetching place details:", error);
        }
    };



    const geocodeAddress = async (address) => {
        try {
            const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
            const response = await axios.get(
                `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
            );
            if (response.data.status === 'OK') {
                const location = response.data.results[0];
                const components = location.address_components;
                const city = components.find((c) => c.types.includes('locality'))?.long_name || '';
                const state = components.find((c) => c.types.includes('administrative_area_level_1'))?.short_name || '';
                const zip = components.find((c) => c.types.includes('postal_code'))?.long_name || '';

                // Normalize city name to capitalize only the first letter
                const normalizedCity = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();

                return { city: normalizedCity, state, zip };
            } else {
                throw new Error('Failed to geocode address');
            }
        } catch (error) {
            console.error('Geocoding error:', error);
            throw new Error('Failed to geocode address');
        }
    };

    const calculateAge = (birthdate) => {
        const birthDate = new Date(birthdate);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();
        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const validateReferralCode = async (code) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/validate-referral-code`, { referralCode: code });
            return response.data.isValid;
        } catch (error) {
            console.error('Error validating referral code:', error);
            return false;
        }
    };

    const handleOpenWaiverDialog = () => {
        setFormData({ ...formData, waiverDialogOpen: true });
    };

    const handleCloseWaiverDialog = () => {
        setFormData({ ...formData, waiverDialogOpen: false });
    };

    const handleWaiverConsent = () => {
        const age = calculateAge(formData.birthdate);
        const isMinor = age < 18;

        // Validation within the dialog
        if (!formData.participantSignatureName.trim()) {
            alert('Please enter the participant\'s full name to sign.');
            return;
        }
        if (isMinor && !formData.guardianSignatureName.trim()) {
            alert('A parent or guardian must also provide their full name.');
            return;
        }
        if (!formData.waiverAgreed) {
            alert('You must check the box to agree to the terms.');
            return;
        }

        // If validation passes, mark as signed and close the dialog
        setFormData(prev => ({
            ...prev,
            waiverSigned: true,
            waiverDialogOpen: false,
        }));
    };

    const handleSubmit = async () => {
        // Clear previous errors
        setErrors({ zipCode: '', referralError: '', emailError: '', sports: '', country: '' });

        // Clear form-specific errors
        setFormData({ ...formData, emailError: '' });

        // 🚫 Validate Required Fields
        if (!formData.zipCode) {
            setErrors((prevErrors) => ({ ...prevErrors, zipCode: 'Zip Code is required' }));
            return;
        }

        if (!formData.country) {
            setErrors((prevErrors) => ({ ...prevErrors, country: 'Country is required' }));
            return;
        }

        if (formData.sports.length === 0) {
            setErrors((prevErrors) => ({ ...prevErrors, sports: 'At least one sport must be selected' }));
            return;
        }

        if (!formData.consent) {
            alert('You must agree to receive messages.');
            return;
        }

        if (!formData.waiverSigned || !formData.waiverConsent) {
            alert('You must agree to the waiver.');
            return;
        }

        if (!formData.birthdate || isNaN(new Date(formData.birthdate))) {
            alert('Valid birthdate is required');
            return;
        }

        // 🕵️ Validate referral code if provided
        if (formData.referralCode && !(await validateReferralCode(formData.referralCode))) {
            setErrors((prevErrors) => ({ ...prevErrors, referralError: 'Invalid referral code' }));
            return;
        }

        let firebaseUid = null;
        let userData = {};

        try {
            console.log('Starting form submission...');


            // 🧠 Birthdate validation
            const birthdate = new Date(formData.birthdate);
            if (!formData.birthdate || isNaN(birthdate.getTime())) {
                setErrors((prevErrors) => ({ ...prevErrors, birthdate: 'Valid birthdate is required' }));
                return;
            }

            // 📞 Format the phone number using libphonenumber-js
            const phoneNumber = parsePhoneNumberFromString(formData.phone, 'US');
            const formattedPhone = phoneNumber ? phoneNumber.format('E.164') : null;

            if (!formattedPhone) {
                setErrors((prevErrors) => ({ ...prevErrors, phone: 'Invalid phone number format' }));
                return;
            }

            console.log('Formatted Phone:', formattedPhone);

            // 🔐 Step 1: Authenticate the user with Firebase
            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
            firebaseUid = userCredential.user.uid;
            console.log('User authenticated with Firebase:', firebaseUid);

            const fullStreet = formData.unit ? `${formData.street}, Unit ${formData.unit}` : formData.street;

            // Step 2: Prepare the user data
            userData = {
                firebaseUid,
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formattedPhone,
                birthday: formData.birthdate,
                gender: formData.gender,
                street: fullStreet,
                city: formData.city,
                state: formData.state,
                password: formData.password,
                zipCode: formData.zipCode,
                country: formData.country,
                sports: formData.sports,
                planDetails: {
                    type: selectedPlan === 'Recurring' ? 'subscription' : 'pay_as_you_go',
                    isRecurring: selectedPlan === 'Recurring',
                    pricePerCredit: 37.5,
                    monthlyFee: selectedPlan === 'Recurring' ? 250 : null,
                    creditsPerMonth: selectedPlan === 'Recurring' ? 8 : null,
                },
                injuryWaiverSigned: formData.waiverSigned,
                publicityWaiverSigned: formData.waiverSigned,
                referralCode: formData.referralCode,
            };

            console.log('Form submitted with:', userData);

            // Step 3: Send InitiateCheckout Event to Facebook (Non-critical)
            try {
                const pixelId = '586371507254917';
                const accessToken = 'EAAIckjdekZBgBO3...';
                const apiUrl = `https://graph.facebook.com/v21.0/${pixelId}/events`;

                const eventData = {
                    data: [
                        {
                            event_name: 'InitiateCheckout',
                            event_time: Math.floor(Date.now() / 1000),
                            event_source_url: window.location.href,
                            action_source: 'website',
                            user_data: {
                                em: formData.email,
                                ph: formattedPhone,
                                fn: formData.firstName.toLowerCase(),
                                ln: formData.lastName.toLowerCase(),
                                ge: formData.gender.toLowerCase(),
                                db: formData.birthdate,
                                ct: formData.city.toLowerCase(),
                                st: formData.state.toLowerCase(),
                                zp: formData.zipCode,
                                country: formData.country.toLowerCase(),
                                client_user_agent: navigator.userAgent,
                            },
                        },
                    ],
                };

                await axios.post(apiUrl, eventData, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                console.log('InitiateCheckout Event Sent Successfully to Facebook');
            } catch (fbError) {
                console.error('Facebook API failed:', fbError.message);
            }

            // Step 4: Send the user data to the backend
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/create-account`, userData);

            if (response.status === 201) {
                console.log('Account creation successful:', response.data);

                // Step 5: Generate and send PDF with the typed signature text
                const age = calculateAge(formData.birthdate);

                const pdfPayload = {
                    userName: `${formData.firstName} ${formData.lastName}`,
                    email: formData.email,
                    participantSignatureText: formData.participantSignatureName,
                    guardianSignatureText: age < 18 ? formData.guardianSignatureName : null,
                    isMinor: age < 18,
                };


                console.log('Sending data for PDF generation:', pdfPayload);

                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/pdf/generate`, pdfPayload);

                alert('Your account has been created. A copy of your signed waiver will be emailed to you shortly.');

                // Step 6: Navigate to the setup-payment page
                navigate(`/setup-payment`, { state: { selectedPlan, user: response.data.user } });
            } else {
                console.error('Account creation failed:', response.data);
                alert(`Account creation failed: ${response.data.message}`);
                throw new Error('Backend user creation failed');
            }
        } catch (error) {
            console.error('Account creation failed:', error);

            // Check for specific location error and redirect to waitlist if found
            if (error.response && error.response.status === 400 && error.response.data.error === 'Location not covered') {
                console.log('Location not covered, deleting incomplete Firebase account and redirecting to waitlist');

                // Delete Firebase user if it was created
                if (firebaseUid) {
                    try {
                        await auth.currentUser.delete();
                        console.log('Incomplete Firebase account deleted');
                    } catch (deleteError) {
                        console.error('Failed to delete Firebase user:', deleteError);
                    }
                }

                navigate('/waitlist', { state: { cityName: formData.city, waitlistData: userData } });
                return;
            }

            // Only delete the Firebase user for critical errors
            if (firebaseUid) {
                if (
                    error.code === 'auth/email-already-in-use' ||
                    error.message.includes('Invalid email') ||
                    (error.response && error.response.status === 400 && error.response.data.error === 'Location not covered')
                ) {
                    try {
                        await auth.currentUser.delete();
                        console.log('Critical error or location not covered. Firebase account deleted.');
                    } catch (deleteError) {
                        console.error('Failed to delete Firebase user:', deleteError);
                    }
                } else {
                    console.warn('Non-critical error during account creation. Firebase account retained.');
                }
            }


            if (error.code === 'auth/email-already-in-use') {
                setFormData({ ...formData, emailError: 'This email is already registered.' });
            } else if (error.message.includes('IndexedDB')) {
                alert('There was a problem accessing the database. Please try again.');
            } else {
                alert(`Account creation failed: ${error.message}`);
            }
        }
    };



    return (
        <Root>


            <StyledContainer maxWidth="sm">
                <StepIndicator>
                    <CheckCircleIcon style={{ marginRight: '0.5rem', color: '#C63D57', fontSize: '2rem' }} />
                    <Typography variant="h5" style={{ fontSize: '2rem' }}>Step 2 of 3</Typography>
                </StepIndicator>
                <PlanIndicator>
                    <Typography variant="h6" style={{ fontSize: '1.2rem', color: '#C63D57' }}>
                        Selected Plan: {selectedPlan === 'Recurring' ? 'Recurring' : 'Pay As You Go'}
                    </Typography>
                </PlanIndicator>
                <Typography variant="h4" gutterBottom>
                    Create an Account
                </Typography>

                <FormContainer>
                    <StyledTextField
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: '#fff' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                    />
                    <StyledTextField
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: '#fff' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                    />
                    <StyledTextField
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: '#fff' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                        error={!!formData.emailError}
                        helperText={formData.emailError}
                    />
                    <StyledTextField
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: '#fff' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                    />
                    <StyledTextField
                        label="Create a password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: '#fff' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                    />
                    <StyledTextField
                        label="Birthdate"
                        name="birthdate"
                        type="date"
                        value={formData.birthdate}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: '#fff' },
                            shrink: true,
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                    />
                    <Box display="flex" flexDirection="column" alignItems="center" mt={2} mb={2}>
                        <Button
                            variant="outlined"
                            style={{ color: '#C63D57', borderColor: '#C63D57' }}
                            onClick={() => setSportsModalOpen(true)}
                        >
                            Select Sports ({formData.sports.length})
                        </Button>
                        <Typography variant="body2" style={{ color: '#fff' }}>
                            {formData.sports.join(', ') || 'No sports selected'}
                        </Typography>
                        {errors.sports && (
                            <Typography variant="body2" style={{ color: 'red' }}>
                                {errors.sports}
                            </Typography>
                        )}
                    </Box>


                    <SelectSportsModal
                        open={sportsModalOpen}
                        onClose={() => setSportsModalOpen(false)}
                        availableSports={availableSports}
                        selectedSports={formData.sports}
                        onSportToggle={toggleSportSelection}
                    />


                    <FormControl fullWidth variant="filled" margin="dense">
                        <InputLabel style={{ color: '#fff' }}>Gender</InputLabel>
                        <Select
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                            style={{ color: '#fff' }}
                        >
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="non-binary">Non-Binary</MenuItem>
                            <MenuItem value="prefer not to say">Prefer Not to Say</MenuItem>
                        </Select>
                    </FormControl>
                    <div style={{ position: "relative", width: "100%" }}>
                        <StyledTextField
                            label="Street"
                            name="street"
                            value={formData.street}
                            onChange={(e) => {
                                handleInputChange(e);
                                handleAddressInput(e.target.value);
                            }}
                            fullWidth
                            margin="dense"
                            variant="filled"
                            InputLabelProps={{
                                style: { color: "#fff" },
                            }}
                            InputProps={{
                                style: { color: "#fff" },
                            }}
                        />

                        {/* ✅ Address Suggestions Dropdown */}
                        {showSuggestions && addressSuggestions.length > 0 && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    width: "100%",
                                    backgroundColor: "white",
                                    border: "1px solid #ccc",
                                    borderRadius: "5px",
                                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                                    zIndex: 1000,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    padding: "5px",
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                            >
                                {addressSuggestions.map((suggestion, index) => (
                                    <Box
                                        key={index}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleAddressSelect(suggestion.place_id);
                                        }}
                                        sx={{
                                            padding: "10px",
                                            cursor: "pointer",
                                            borderBottom: "1px solid #ddd",
                                            color: "black",
                                            backgroundColor: index % 2 === 0 ? "#f9f9f9" : "white",
                                            "&:hover": { backgroundColor: "#e0e0e0" },
                                        }}
                                    >
                                        {suggestion.description || "No description available"}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </div>
                    <StyledTextField
                        label="Unit # (optional)"
                        name="unit"
                        value={formData.unit}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{ style: { color: '#fff' } }}
                        InputProps={{ style: { color: '#fff' } }}
                    />
                    <StyledTextField
                        label="City"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: '#fff' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                    />
                    <StyledTextField
                        label="State"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: '#fff' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                    />
                    <StyledTextField
                        label="Zip Code"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        required
                        InputLabelProps={{
                            style: { color: '#fff' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                        error={!!errors.zipCode}
                        helperText={errors.zipCode}
                    />
                    <StyledTextField
                        label="Country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{ style: { color: '#fff' } }}
                        InputProps={{ style: { color: '#fff' } }}
                        error={!!errors.country}
                        helperText={errors.country}
                    />

                    <StyledTextField
                        label="Referral Code (optional)"
                        name="referralCode"
                        value={formData.referralCode}
                        onChange={handleInputChange}
                        fullWidth
                        margin="dense"
                        variant="filled"
                        InputLabelProps={{
                            style: { color: '#fff' },
                        }}
                        InputProps={{
                            style: { color: '#fff' },
                        }}
                        error={!!formData.referralError}
                        helperText={formData.referralError}
                    />
                    <FormControlLabel
                        control={<Checkbox name="consent" checked={formData.consent} onChange={handleInputChange} sx={{ color: '#C63D57', '&.Mui-checked': { color: '#C63D57' } }} />}
                        label={<ConsentLabel>I agree to receive messages about my account and service updates.</ConsentLabel>}
                        style={{ color: '#fff' }}
                    />
                    <FormControlLabel
                        control={<Checkbox name="waiverConsent" checked={formData.waiverConsent} onChange={handleInputChange} disabled={!formData.waiverSigned} sx={{ color: '#C63D57', '&.Mui-checked': { color: '#C63D57' } }} />}
                        label={<ConsentLabel>I agree to the injury and publicity waiver.</ConsentLabel>}
                        style={{ color: '#fff' }}
                    />
                    <Box>
                        <Button variant="text" style={{ color: '#C63D57' }} onClick={handleOpenWaiverDialog}>
                            Read Waiver
                        </Button>
                    </Box>
                    <ActionButton fullWidth variant="contained" onClick={handleSubmit}>
                        Next
                    </ActionButton>
                </FormContainer>
            </StyledContainer>
            <Typography variant="body2" style={{ color: '#fff', marginTop: '0.1rem', marginBottom: '2rem', textAlign: 'center' }}>
                Questions? Email us: <a href="mailto:info@joinunited.ai" style={{ color: '#C63D57', textDecoration: 'none' }}>info@joinunited.ai</a>
            </Typography>
            <Footer>
                © 2025 United Artificial Intelligence Company, Inc. All rights reserved.
            </Footer>
            {/* --- WAIVER DIALOG --- */}
            <Dialog
                open={formData.waiverDialogOpen}
                onClose={handleCloseWaiverDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgba(28, 28, 30, 0.85)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid',
                        borderColor: 'rgba(198, 61, 87, 0.2)',
                        borderRadius: '16px',
                        color: '#EAEAEB',
                        boxShadow: '0px 8px 40px rgba(0, 0, 0, 0.5)',
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontWeight: 'bold',
                    fontSize: '1.5rem',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid rgba(198, 61, 87, 0.2)',
                    p: 3,
                }}>
                    <CheckCircleIcon sx={{ color: '#C63D57', fontSize: '2rem' }} />
                    Injury & Publicity Waiver
                </DialogTitle>
                <DialogContent sx={{
                    p: 3,
                    '&::-webkit-scrollbar': { width: '8px' },
                    '&::-webkit-scrollbar-track': { background: 'rgba(255, 255, 255, 0.05)' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#C63D57', borderRadius: '4px' },
                    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a83348' }
                }}>
                    <Typography variant="body1" sx={{ mb: 3, color: '#A0A0A5', fontSize: '0.95rem' }}>
                        To proceed, please review the terms. Your digital signature confirms your consent. For participants under 18, a parent or legal guardian's digital signature is also required.
                    </Typography>

                    {/* Enhanced Waiver Text Box */}
                    <Box sx={{ height: '300px', overflowY: 'auto', border: '1px solid transparent',
                        background: 'linear-gradient(rgba(28, 28, 30, 1), rgba(28, 28, 30, 1)) padding-box, linear-gradient(145deg, rgba(198, 61, 87, 0.5), rgba(198, 61, 87, 0.1)) border-box',
                        borderRadius: '12px', p: 2, mb: 2
                    }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.6, color: '#C7C7CC' }}>
                            {`I hereby assume all of the risks of participating in Mars Reel Academy training sessions, including by way of example and not limitation, any risks that may arise from negligence or carelessness on the part of the persons or entities being released, from dangerous or defective equipment or property owned, maintained or controlled by them, or because of their possible liability without fault. I certify that I am physically fit and have not been advised to not participate by a qualified medical professional. I certify that there are no health-related reasons or problems which preclude my participation in Mars Reel Academy. I acknowledge that this Accident Waiver and Release of Liability Form will be used by Mars Reel Media Corp. in which I may participate and that it will govern my actions and responsibilities at Mars Reel Academy training sessions.

In consideration of my application and permitting me to participate in Mars Reel Academy, I hereby take action for myself, my executors, administrators, heirs, next of kin, successors, and assigns as follows: (A) I WAIVE, RELEASE, AND DISCHARGE from any and all liability, including but not limited to, liability arising from the negligence or fault of the entities or persons released, for my death, disability, personal injury, property damage, property theft, or actions of any kind which may hereafter occur to me including my traveling to and from Mars Reel Academy sessions. THE FOLLOWING ENTITIES OR PERSONS: Mars Reel Media Corporation, and/or their subsidiaries, directors, officers, employees, coaches, trainers, agents, representatives or volunteers. (B) I INDEMNIFY, HOLD HARMLESS, AND PROMISE NOT TO SUE the entities or persons mentioned in this paragraph from any and all liabilities or claims made as a result of participation in Mars Reel Academy, whether caused by negligence or otherwise. I acknowledge that Mars Reel Academy may carry with it the potential for death, serious injury, and personal loss. The risks may include, but are not limited to, those caused by terrain, facilities, temperature, weather, condition of participants, equipment, vehicular traffic, actions of other people including, but not to, participants, volunteers, spectators, coaches, and lack of hydration.

I consent and agree that Mars Reel Media Corporation and/or their employees, directors, officers, coaches, agents, representatives or volunteers may take photographs or digital recordings of me as a participant during this event and use these in any and all media for training or promotional purposes. I further consent that my identity may be revealed therein or by description text or commentary. I waive any rights, claims or interest and I understand that there will be no financial or other remuneration. This accident waiver, release of liability and image release shall be construed broadly to provide a release and waiver to the maximum extent permissible under applicable law. 

I CERTIFY THAT I HAVE READ THIS DOCUMENT, AND I FULLY UNDERSTAND ITS CONTENT. I AM AWARE THAT THIS IS A RELEASE OF LIABILITY AND A CONTRACT AND I SIGN IT ON MY OWN FREE WILL.`}
                        </Typography>
                    </Box>

                    {/* Enhanced Input Fields */}
                    <TextField
                        autoFocus
                        margin="dense"
                        name="participantSignatureName"
                        label="Participant's Full Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={formData.participantSignatureName}
                        onChange={handleInputChange}
                        helperText="Typing your full name constitutes a legal digital signature."
                        sx={{
                            '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255, 255, 255, 0.2)' },
                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#C63D57' },
                            '& .MuiInputLabel-root': { color: '#A0A0A5' },
                            '& .MuiInput-root': { color: '#FFFFFF' },
                            '& .MuiFormHelperText-root': { color: '#A0A0A5' }
                        }}
                    />

                    {calculateAge(formData.birthdate) < 18 && (
                        <TextField
                            margin="dense"
                            name="guardianSignatureName"
                            label="Parent/Guardian's Full Name"
                            type="text"
                            fullWidth
                            variant="standard"
                            value={formData.guardianSignatureName}
                            onChange={handleInputChange}
                            helperText="Required for participants under 18. This constitutes a legal digital signature."
                            sx={{
                                mt: 2,
                                '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255, 255, 255, 0.2)' },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#C63D57' },
                                '& .MuiInputLabel-root': { color: '#A0A0A5' },
                                '& .MuiInput-root': { color: '#FFFFFF' },
                                '& .MuiFormHelperText-root': { color: '#A0A0A5' }
                            }}
                        />
                    )}

                    {/* Enhanced Checkbox */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="waiverAgreed"
                                checked={formData.waiverAgreed}
                                onChange={handleInputChange}
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    '&.Mui-checked': {
                                        color: '#C63D57',
                                    },
                                }}
                            />
                        }
                        label="I have read, understood, and digitally agree to the terms of the waiver."
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{
                    p: 3,
                    borderTop: '1px solid rgba(198, 61, 87, 0.2)',
                    justifyContent: 'space-between',
                }}>
                    <Button onClick={handleCloseWaiverDialog} sx={{ color: '#A0A0A5' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleWaiverConsent}
                        variant="contained"
                        sx={{
                            backgroundColor: '#C63D57',
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            px: 4,
                            py: 1.5,
                            '&:hover': {
                                backgroundColor: '#a83348',
                                boxShadow: '0 0 15px rgba(198, 61, 87, 0.6)',
                            },
                        }}
                    >
                        Agree & Finalize Signature
                    </Button>
                </DialogActions>
            </Dialog>
        </Root>
    );
};

export default CreateAccount;
